import React, { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import SelectItemModal from './SelectItemModal';
import KlydoCard from '../../../Klydos/KlydosList/components/KlydoCard';
import useSelectableKlydosInfiniteQuery from '../../hooks/useSelectableKlydosInfiniteQuery';
import { PopulatedKlydo } from '../../../../api/klydo';
import DSTextField from '../../../../design-system/DSTextField';

interface SelectKlydosModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (selectedKlydos: PopulatedKlydo[]) => void;
  currentSelectedKlydos: PopulatedKlydo[];
  collectionId?: string;
  title?: string;
  subtitle?: string;
  maxSelection?: number;
  confirmButtonText?: string;
}

const SelectKlydosModal = ({
  isOpen,
  onClose,
  onConfirm,
  currentSelectedKlydos,
  collectionId,
  title = 'Add a Klydo',
  subtitle = 'Click to choose the Klydo you want to add to your collection',
  maxSelection,
  confirmButtonText = 'Add',
}: SelectKlydosModalProps) => {
  const queryClient = useQueryClient();
  const [searchText, setSearchText] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchText), 300);
    return () => clearTimeout(timer);
  }, [searchText]);

  const handleClose = () => {
    setSearchText('');
    onClose();
  };

  const {
    data: klydos,
    isLoading,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
  } = useSelectableKlydosInfiniteQuery({
    collectionId,
    searchText: debouncedSearch,
  });

  const handleConfirm = (selectedIds: string[]) => {
    const allKlydos = [...(klydos || []), ...currentSelectedKlydos];
    const uniqueKlydosMap = new Map(allKlydos.map((k) => [k.id, k]));

    const selectedKlydos = selectedIds
      .map(
        (id) =>
          uniqueKlydosMap.get(id) ??
          queryClient.getQueryData<PopulatedKlydo>(['klydo', id]),
      )
      .filter((k): k is PopulatedKlydo => k !== undefined);

    onConfirm(selectedKlydos);
  };

  const renderKlydoItem = (
    klydo: PopulatedKlydo,
    isSelected: boolean,
    onToggle: () => void,
  ) => (
    <KlydoCard
      klydo={klydo}
      isSelected={isSelected}
      onSelect={() => onToggle()}
      isStatic
      isMenuHidden
    />
  );

  const searchBar = (
    <DSTextField
      value={searchText}
      onChange={(e) => setSearchText(e.target.value)}
      placeholder="Search klydos..."
      fullWidth
      size="small"
      preventEmojis={false}
    />
  );

  return (
    <SelectItemModal<PopulatedKlydo>
      isOpen={isOpen}
      onClose={handleClose}
      title={title}
      subtitle={subtitle}
      searchBar={searchBar}
      items={klydos || []}
      getItemId={(klydo) => klydo.id}
      selectedItemIds={currentSelectedKlydos.map((k) => k.id)}
      onConfirm={handleConfirm}
      renderItem={renderKlydoItem}
      gridColumns={2}
      maxSelection={maxSelection}
      confirmButtonText={confirmButtonText}
      isLoading={isLoading}
      emptyMessage="No Klydos available."
      fetchNextPage={fetchNextPage}
      isFetchingNextPage={isFetchingNextPage}
      hasNextPage={hasNextPage}
    />
  );
};

export default SelectKlydosModal;
